import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react'
import { cn } from '@/lib/utils'

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'subtle'
type Size = 'sm' | 'md' | 'lg' | 'icon'

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: Variant
  size?: Size
  loading?: boolean
  leftIcon?: ReactNode
  rightIcon?: ReactNode
  /**
   * When provided, the button is disabled and this string is shown as a native
   * tooltip (via `title`) and `aria-disabled` so screen readers announce it.
   * Useful for permission-gated buttons — e.g. `disabledReason="Sem permissão closing.balcao.edit nesta unidade"`.
   * Takes precedence over `disabled`.
   */
  disabledReason?: string
}

const variantStyles: Record<Variant, string> = {
  primary:
    'bg-brand text-white hover:bg-brand-hover disabled:opacity-60 disabled:hover:bg-brand',
  secondary:
    'border border-border-color bg-surface text-foreground hover:bg-surface-alt disabled:opacity-60',
  ghost:
    'text-foreground-secondary hover:bg-surface-alt hover:text-foreground disabled:opacity-60',
  danger:
    'border border-danger-border bg-danger-soft text-danger hover:border-danger disabled:opacity-60',
  subtle:
    'border border-border-color bg-surface-alt text-foreground hover:bg-background disabled:opacity-60',
}

const sizeStyles: Record<Size, string> = {
  sm: 'h-8 px-3 text-xs',
  md: 'h-10 px-4 text-sm',
  lg: 'h-11 px-5 text-sm',
  icon: 'h-8 w-8 p-0',
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = 'primary',
      size = 'md',
      loading = false,
      leftIcon,
      rightIcon,
      disabled,
      disabledReason,
      children,
      type = 'button',
      title,
      ...rest
    },
    ref
  ) => {
    const isPermissionBlocked = Boolean(disabledReason)
    const finalDisabled = disabled || loading || isPermissionBlocked
    return (
      <button
        ref={ref}
        type={type}
        disabled={finalDisabled}
        aria-disabled={finalDisabled || undefined}
        title={disabledReason ?? title}
        className={cn(
          'inline-flex items-center justify-center gap-2 rounded-md font-medium outline-none transition-colors focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-1',
          variantStyles[variant],
          sizeStyles[size],
          isPermissionBlocked && 'cursor-not-allowed',
          className
        )}
        {...rest}
      >
        {loading ? (
          <span
            aria-hidden
            className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent"
          />
        ) : (
          leftIcon
        )}
        {children}
        {!loading && rightIcon}
      </button>
    )
  }
)

Button.displayName = 'Button'
